/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Drawer,
  Grid,
  IconButton,
  Typography,
  Divider,
  Chip,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ChevronDown, ChevronUp } from 'lucide-react';
import MapIcon from "@mui/icons-material/Map";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Badge } from "../ui/badge";
import { Icon } from "../Shared/Icon";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { IRemoteEquipment } from "../../store/api/devices/types";
import StatusIcon from "../Shared/Status/StatusIcon";
import { resolveDeviceStatusLabel } from "../../utils/deviceStatus";
import { parseDeviceAttributes } from "./DasDeviceDetails/utils";
import { AU2_TYPE_ALIASES, RU2_TYPE_ALIASES, E2O_TYPE_ALIASES, AU3_TYPE_ALIASES, RU3_TYPE_ALIASES, E3_TYPE_ALIASES, matchesMa6200Alias, MA3000_TYPE_ALIASES, matchesMa3000Alias } from "./DasDeviceDetails/DasDevTypeAliases";
import DetailRow from "./DasDeviceDetails/components/DetailRow";
import RimDeviceDetails from "./DasDeviceDetails/devices/RimDeviceDetails";
import AcmDeviceDetails from "./DasDeviceDetails/devices/AcmDeviceDetails";
import HcmDeviceDetails from "./DasDeviceDetails/devices/HcmDeviceDetails";
import OimDeviceDetails from "./DasDeviceDetails/devices/OimDeviceDetails";
import RauDeviceDetails from "./DasDeviceDetails/devices/RauDeviceDetails";
import RxuDeviceDetails from "./DasDeviceDetails/devices/RxuDeviceDetails";
import MruDeviceDetails from "./DasDeviceDetails/devices/MruDeviceDetails";
import MxuDeviceDetails from "./DasDeviceDetails/devices/MxuDeviceDetails";
import FcmDeviceDetails from "./DasDeviceDetails/devices/FcmDeviceDetails";
import OchDeviceDetails from "./DasDeviceDetails/devices/OchDeviceDetails";
import DxruDeviceDetails from "./DasDeviceDetails/devices/DxruDeviceDetails";
import Dxru10gDeviceDetails from "./DasDeviceDetails/devices/Dxru10gDeviceDetails";
import PsuDeviceDetails from "./DasDeviceDetails/devices/PsuDeviceDetails";
import SpsuDeviceDetails from "./DasDeviceDetails/devices/SpsuDeviceDetails";
import AuDeviceDetails from "./DasDeviceDetails/devices/AuDeviceDetails";
import A3DeviceDetails from "./DasDeviceDetails/devices/A3DeviceDetails";
import Ru2DeviceDetails from "./DasDeviceDetails/devices/Ru2DeviceDetails";
import Ru3DeviceDetails from "./DasDeviceDetails/devices/Ru3DeviceDetails";
import E2DeviceDetails from "./DasDeviceDetails/devices/E2DeviceDetails";
import E3DeviceDetails from "./DasDeviceDetails/devices/E3DeviceDetails";
import DeuDeviceDetails from "./DasDeviceDetails/devices/DeuDeviceDetails";
import DcuDeviceDetails from "./DasDeviceDetails/devices/DcuDeviceDetails";
import SDcuDeviceDetails from "./DasDeviceDetails/devices/SDcuDeviceDetails";
import RiuDeviceDetails from "./DasDeviceDetails/devices/RiuDeviceDetails";
import SRiuDeviceDetails from "./DasDeviceDetails/devices/SRiuDeviceDetails";
import DeviceTopologyPath from "./DeviceTopologyPath";
import { useGetDasInventoryQuery } from "../../store/api/devices/deviceApiSlice";
import { useTriggerDasDiscoveryMutation } from "../../store/api/deviceConfig/deviceConfigApiSlice";


// Extend dayjs with UTC plugin for timezone conversions
dayjs.extend(utc);

interface IDasDeviceDetailsDrawerProps {
  open: boolean;
  device: IRemoteEquipment | null;
  onClose: () => void;
  siteObjectKey?: string;
}

const DasDeviceDetailsDrawer: React.FC<IDasDeviceDetailsDrawerProps> = ({
  open,
  device: initialDevice,
  onClose,
  siteObjectKey,
}) => {
  // Use device's servicesDeviceObjectKey if siteObjectKey not provided
  const deviceKey = siteObjectKey || initialDevice?.servicesDeviceObjectKey;

  // State for managing collapsed sections (must be before any early returns)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    summary: true, // Summary is expanded by default
  });
  
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [selectedObjectKey, setSelectedObjectKey] = useState<string | null>(null);
  const isAutoRefreshInFlight = useRef(false);

  // Fetch DAS inventory for ACM lookup
  const { data: allEquipment = [], refetch: refetchInventory } = useGetDasInventoryQuery(
    { deviceKey: deviceKey || '' },
    { 
      skip: !deviceKey || !open,
      pollingInterval: 0, // Disable automatic polling to prevent unnecessary refetches
      refetchOnMountOrArgChange: false, // Only fetch once when drawer opens
      refetchOnFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: false, // Don't refetch on network reconnect
    }
  );
  const [triggerDasDiscovery] = useTriggerDasDiscoveryMutation();

  const liveDevice = initialDevice
    ? allEquipment.find((eq) => eq.objectKey === initialDevice.objectKey) || initialDevice
    : null;

  useEffect(() => {
    if (!open) {
      setSelectedObjectKey(null);
      return;
    }
    setSelectedObjectKey(initialDevice?.objectKey || null);
  }, [open, initialDevice?.objectKey]);

  const selectedDevice = useMemo(() => {
    if (!liveDevice) return null;
    if (!selectedObjectKey) return liveDevice;
    return allEquipment.find((eq) => eq.objectKey === selectedObjectKey) || liveDevice;
  }, [allEquipment, liveDevice, selectedObjectKey]);

  const device = selectedDevice;

  const statusLabel = resolveDeviceStatusLabel(device?.status);
  const parsedAttributes = parseDeviceAttributes(device?.attributes);

  const formatRefreshLabel = (updatedAt?: string) => {
    if (!updatedAt) {
      return "Never refreshed";
    }
    // Parse the timestamp - handle both epoch milliseconds and ISO strings
    let parsedDate;
    if (typeof updatedAt === 'number' || /^\d+$/.test(updatedAt)) {
      // It's epoch milliseconds since the server stores timestamps as epoch
      parsedDate = dayjs(Number(updatedAt));
    } else {
      // It's an ISO string - parse as UTC and convert to local
      parsedDate = dayjs.utc(updatedAt).local();
    }
    
    if (!parsedDate.isValid()) {
      return "Never refreshed";
    }
    // Format in browser's local timezone
    return parsedDate.format('MMM DD, HH:mm:ss');
  };

  const formatRefreshTooltip = (updatedAt?: string) => {
    if (!updatedAt) {
      return "Last refresh: Never refreshed";
    }
    // Parse the timestamp - handle both epoch milliseconds and ISO strings
    let parsedDate;
    if (typeof updatedAt === 'number' || /^\d+$/.test(updatedAt)) {
      // It's epoch milliseconds
      parsedDate = dayjs(Number(updatedAt));
    } else {
      // It's an ISO string - parse as UTC and convert to local
      parsedDate = dayjs.utc(updatedAt).local();
    }
    
    if (!parsedDate.isValid()) {
      return "Last refresh: Never refreshed";
    }
    // Format in browser's local timezone with full date/time
    return `Last refresh: ${parsedDate.format('YYYY-MM-DD HH:mm:ss')}`;
  };

  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) {
      return "N/A";
    }
    // Parse the timestamp - handle both epoch milliseconds and ISO strings
    let parsedDate;
    if (typeof lastSeen === 'number' || /^\d+$/.test(lastSeen)) {
      // It's epoch milliseconds
      parsedDate = dayjs(Number(lastSeen));
    } else {
      // It's an ISO string - parse as UTC and convert to local
      parsedDate = dayjs.utc(lastSeen).local();
    }
    
    if (!parsedDate.isValid()) {
      return "N/A";
    }
    // Format in browser's local timezone with full date/time
    return parsedDate.format('YYYY-MM-DD HH:mm:ss');
  };

  const triggerPartialDiscovery = useCallback(async () => {
    const equipmentType = liveDevice?.equipmentType;
    if (!deviceKey || !equipmentType || isAutoRefreshInFlight.current) {
      return;
    }

    isAutoRefreshInFlight.current = true;
    try {
      const componentTarget: Record<string, string> = {
        componentType: equipmentType,
      };
      if (liveDevice?.objectKey) {
        componentTarget.objectKey = liveDevice.objectKey;
      }
      if (liveDevice?.equipmentIndex) {
        componentTarget.equipmentIndex = liveDevice.equipmentIndex;
      }
      if (liveDevice?.serialNumber) {
        componentTarget.serialNumber = liveDevice.serialNumber;
      }

      await triggerDasDiscovery({
        deviceKey,
        componentTargets: [componentTarget],
      });
      await refetchInventory();
    } catch (error) {
      console.warn("Failed to auto-trigger component discovery", error);
    } finally {
      isAutoRefreshInFlight.current = false;
    }
  }, [
    deviceKey,
    liveDevice?.equipmentType,
    liveDevice?.objectKey,
    liveDevice?.equipmentIndex,
    liveDevice?.serialNumber,
    triggerDasDiscovery,
    refetchInventory,
  ]);

  useEffect(() => {
    if (!open || !deviceKey || !liveDevice?.equipmentType) {
      return;
    }

    // Trigger discovery once when drawer opens
    triggerPartialDiscovery();

    // Auto-refresh: trigger SNMP polling every 10 seconds
    // Backend will rate-limit to prevent excessive polling (max once per 10 seconds)
    const autoRefreshInterval = window.setInterval(() => {
      triggerPartialDiscovery();
    }, 10000);

    return () => {
      window.clearInterval(autoRefreshInterval);
    };
  }, [open, deviceKey, liveDevice?.equipmentType, triggerPartialDiscovery]);

  // Keep hooks in a stable order before any early return.
  if (!device || !liveDevice) {
    return null;
  }

  // Extract chassis type - look up the ACM device to get chassis type
  const getChassisTypeName = (): string | null => {
    // First try from current device's attributes (works for ACM itself)
    const chassisType = parsedAttributes.acmChassisType || parsedAttributes.chassisType;
    if (chassisType) {
      switch (String(chassisType)) {
        case '1':
          return 'HEU';
        case '2':
          return 'OIU';
        case '3':
          return 'IHU';
        default:
          return null;
      }
    }

    // For non-ACM devices, look up ACM using both objectKey and managedObject
    if (allEquipment.length > 0) {
      let acmDevice: IRemoteEquipment | undefined;
      
      // Strategy 1: Try objectKey-based lookup first (new format)
      if (device.objectKey) {
        const parts = device.objectKey.split('.');
        if (parts.length >= 3) {
          const siteSn = parts[0];
          const deviceType = parts[1];
          const chassisIndex = parts[2];
          // ACM objectKey format: {siteSn}.acm.{chassisIndex}.0.0.0
          const acmObjectKeyPrefix = `${siteSn}.acm.${chassisIndex}`;
          
          acmDevice = allEquipment.find(
            eq => eq.equipmentType === 'ACM' && 
                  eq.objectKey?.startsWith(acmObjectKeyPrefix)
          );
        }
      }
      
      // Strategy 2: Fallback to managedObject-based lookup (if format differs)
      if (!acmDevice && device.managedObject) {
        const parts = device.managedObject.split('.');
        if (parts.length >= 3) {
          const siteSn = parts[0];
          const deviceType = parts[1];
          const chassisIndex = parts[2];
          // ACM managedObject format: {siteSn}.acm.{chassisIndex}.0.0.0
          const acmManagedObjectPrefix = `${siteSn}.acm.${chassisIndex}`;
          
          // Find ACM device - try prefix match
          acmDevice = allEquipment.find(
            eq => eq.equipmentType === 'ACM' && eq.managedObject?.startsWith(acmManagedObjectPrefix)
          );
          
          // Fallback: Match by siteSn and chassisIndex alone
          if (!acmDevice) {
            acmDevice = allEquipment.find(eq => {
              if (eq.equipmentType !== 'ACM' || !eq.managedObject) return false;
              const acmParts = eq.managedObject.split('.');
              // Check if siteSn and chassisIndex match
              return acmParts.length >= 3 && acmParts[0] === siteSn && acmParts[2] === chassisIndex;
            });
          }
        }
      }
      
      // Extract chassis type from ACM device if found
      if (acmDevice?.attributes) {
        const acmAttrs = parseDeviceAttributes(acmDevice.attributes);
        const acmChassisType = acmAttrs.acmChassisType || acmAttrs.chassisType;
        if (acmChassisType) {
          switch (String(acmChassisType)) {
            case '1':
              return 'HEU';
            case '2':
              return 'OIU';
            case '3':
              return 'IHU';
            default:
              return null;
          }
        }
      }
    }
    
    return null;
  };

  const chassisTypeName = getChassisTypeName();

  // Extract temperature and uptime from parsed attributes
  const temperature = parsedAttributes.temperature || parsedAttributes.Temperature;
  const uptime = parsedAttributes.uptime || parsedAttributes.Uptime;
  const hasTemperature = temperature !== undefined && temperature !== null && temperature !== 0 && temperature !== "0";
  const hasUptime = uptime !== undefined && uptime !== null && uptime !== 0 && uptime !== "0";

  // Extract zone info
  const zoneInfo = parsedAttributes.zoneInfo || null;

  // Check if device is an AU/A2 controller based on either equipmentType or devType.
  // MA6200-G1 payloads can carry "Primary A2"/"Secondary A2" in either field.
  const devType = parsedAttributes.devType;
  const equipmentType = (device.equipmentType || "").toUpperCase();
  const devTypeUpper = (devType || "").toUpperCase();

      // first check if device is a Ma3000 device based on either equipmentType or devType.
      const isMa3000Device = matchesMa3000Alias(equipmentType, MA3000_TYPE_ALIASES) || matchesMa3000Alias(devTypeUpper, MA3000_TYPE_ALIASES);
     
      // Check if device is an A2 type (MA6200-G1)
      const isAuDevice = !isMa3000Device && (matchesMa6200Alias(equipmentType, AU2_TYPE_ALIASES) || matchesMa6200Alias(devTypeUpper, AU2_TYPE_ALIASES));

      // Check if device is an RU2 type based on either equipmentType or devType for MA6200G1 devices
      const isRu2Device = !isMa3000Device && (matchesMa6200Alias(equipmentType, RU2_TYPE_ALIASES) || matchesMa6200Alias(devTypeUpper, RU2_TYPE_ALIASES));
      // Check if device is an RU3 type (MA6200-G2)
      const isRu3Device = !isMa3000Device && !isRu2Device && (matchesMa6200Alias(equipmentType, RU3_TYPE_ALIASES) || matchesMa6200Alias(devTypeUpper, RU3_TYPE_ALIASES));

      // Check if device is an E2 type based on either equipmentType or devType for MA6200G1 devices
      const isE2Device = !isMa3000Device && (matchesMa6200Alias(equipmentType, E2O_TYPE_ALIASES) || matchesMa6200Alias(devTypeUpper, E2O_TYPE_ALIASES));

      // Check if device is an E3 type (MA6200-G2)
      const isE3Device = !isMa3000Device && (matchesMa6200Alias(equipmentType, E3_TYPE_ALIASES) || matchesMa6200Alias(devTypeUpper, E3_TYPE_ALIASES));

      // Check if device is an A3 type (MA6200-G2)
      const isA3Device = !isMa3000Device && (matchesMa6200Alias(equipmentType, AU3_TYPE_ALIASES) || matchesMa6200Alias(devTypeUpper, AU3_TYPE_ALIASES));
 

  const equipmentTypeUpper = equipmentType;
  const isDxru10gDevice =
    equipmentTypeUpper === "DXRU10G" ||
    equipmentTypeUpper === "DXRU-10G" ||
    parsedAttributes?.dxRU10GDeviceInfoIndex !== undefined;

  const isDeuDevice =
    (device.equipmentType || "").toUpperCase() === "DEU" ||
    parsedAttributes?.deuDeviceInfoIndex !== undefined ||
    parsedAttributes?.deuAlarms !== undefined ||
    parsedAttributes?.deuAlarmsMask !== undefined;

  const isRiuDevice =
    equipmentTypeUpper === "RIU" ||
    equipmentTypeUpper === "SRIU" ||
    equipmentTypeUpper === "S-RIU" ||
    parsedAttributes?.riuIndex !== undefined ||
    parsedAttributes?.sriuIndex !== undefined;

  const topologyPathSupportedTypes = [
    'HCM',
    'ACM',
    'OIM',
    'RIM',
    'FCM',
    'FMM',
    'FRM',
    'DCU',
    'RIU',
    'DEU',
    'PSU',
    'SPSU',
    'S-PSU',
    'DXRU',
    'DXRU10G',
    'RU3',
    'E3',
    'E2',
    'A2',
    'A3',
    'M2',
    'M3',
    'N2',
    'N3',
  ];

  const isTopologyType = topologyPathSupportedTypes.some((type) =>
    (device?.equipmentType ?? '').toUpperCase().includes(type)
  );

  const hasLegacyTopologyObjectKey =
    !!device.objectKey &&
    device.objectKey.includes('_') &&
    !device.objectKey.endsWith('_0_0.0.0.0');

  const shouldShowTopologyPath =
    ((device.isRemote || isTopologyType) && !!(device.managedObject || device.objectKey)) ||
    hasLegacyTopologyObjectKey;

  const handleAccordionChange = (section: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSections((prev) => ({ ...prev, [section]: isExpanded }));
  };

  // Render consolidated Summary Card with all device information
  const renderSummary = () => {
    const isMa6200 = device.model?.toLowerCase().includes('6200');
    return (
      <Card className="mb-2">
        {!isMa6200 && (
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Summary</CardTitle>
          </CardHeader>
        )}
        <CardContent className={`px-4 pb-4 ${isMa6200 ? 'pt-4' : 'pt-0'}`}>
          {/* Always visible: Model and Software Version in 2 columns */}
          <Grid container spacing={3}>
            {device.model && (
              <Grid item xs={6}>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Model</p>
                <p className="text-sm text-foreground">{device.model}</p>
              </Grid>
            )}
            {(device.swVersion || parsedAttributes.swVersion) && (
              <Grid item xs={6}>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Software Version</p>
                <p className="text-sm text-foreground">{device.swVersion || parsedAttributes.swVersion}</p>
              </Grid>
            )}
          </Grid>

          {/* Collapsible additional information */}
          <Collapse in={showMoreInfo}>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Basic Information */}
              {device.hostname && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Name</p>
                  <p className="text-sm text-foreground">{device.hostname}</p>
                </Grid>
              )}
              {device.managedObject && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Managed Object</p>
                  <p className="text-sm text-foreground">{device.managedObject}</p>
                </Grid>
              )}
              {device.equipmentType && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Equipment Type</p>
                  <p className="text-sm text-foreground">{device.equipmentType}</p>
                </Grid>
              )}
              {device.band && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Band</p>
                  <p className="text-sm text-foreground">{device.band}</p>
                </Grid>
              )}
              {device.equipmentIndex && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Equipment Index</p>
                  <p className="text-sm text-foreground">{device.equipmentIndex}</p>
                </Grid>
              )}
              {zoneInfo && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Zone</p>
                  <p className="text-sm text-foreground">{zoneInfo}</p>
                </Grid>
              )}
              {temperature && !isE2Device && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Temperature</p>
                  <p className="text-sm text-foreground">{temperature}</p>
                </Grid>
              )}

              {/* Hardware Information */}
              {device.vendor && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Vendor</p>
                  <p className="text-sm text-foreground">{device.vendor}</p>
                </Grid>
              )}
              {device.serialNumber && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Serial Number</p>
                  <p className="text-sm text-foreground">{device.serialNumber}</p>
                </Grid>
              )}
              {device.assetTag && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Asset Tag</p>
                  <p className="text-sm text-foreground">{device.assetTag}</p>
                </Grid>
              )}
              {device.hwVersion && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Hardware Version</p>
                  <p className="text-sm text-foreground">{device.hwVersion}</p>
                </Grid>
              )}

              {/* Network Information */}
              {device.ipAddress && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">IP Address</p>
                  <p className="text-sm text-foreground">{device.ipAddress}</p>
                </Grid>
              )}
              {device.macAddress && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">MAC Address</p>
                  <p className="text-sm text-foreground">{device.macAddress}</p>
                </Grid>
              )}

              {/* Location Information */}
              {device.location && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Location</p>
                  <p className="text-sm text-foreground">{device.location}</p>
                </Grid>
              )}
              {device.geoRegion && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Geo Region</p>
                  <p className="text-sm text-foreground">{device.geoRegion}</p>
                </Grid>
              )}
              {device.siteGroup && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Site Group</p>
                  <p className="text-sm text-foreground">{device.siteGroup}</p>
                </Grid>
              )}
              {device.geoLatitude && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Latitude</p>
                  <p className="text-sm text-foreground">{device.geoLatitude}</p>
                </Grid>
              )}
              {device.geoLongitude && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Longitude</p>
                  <p className="text-sm text-foreground">{device.geoLongitude}</p>
                </Grid>
              )}

              {/* Maintenance Information */}
              {device.installationDate && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Installation Date</p>
                  <p className="text-sm text-foreground">{device.installationDate}</p>
                </Grid>
              )}
              {device.warrantyExpiry && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Warranty Expiry</p>
                  <p className="text-sm text-foreground">{device.warrantyExpiry}</p>
                </Grid>
              )}
              {device.lastAuditDate && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Last Audit Date</p>
                  <p className="text-sm text-foreground">{device.lastAuditDate}</p>
                </Grid>
              )}
              {device.lastSeen && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Last Update</p>
                  <p className="text-sm text-foreground">{formatLastSeen(device.lastSeen)}</p>
                </Grid>
              )}
              {uptime && (
                <Grid item xs={6}>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Uptime (seconds)</p>
                  <p className="text-sm text-foreground">{uptime}</p>
                </Grid>
              )}
            </Grid>
          </Collapse>

          {/* Show More Button */}
          <div className="mt-3 text-center">
            <button
              onClick={() => setShowMoreInfo(!showMoreInfo)}
              className="inline-flex items-center gap-1 text-sm text-foreground hover:opacity-70 transition-opacity bg-transparent border-0 p-0 cursor-pointer"
            >
              {showMoreInfo ? (
                <><ChevronUp className="w-4 h-4" />Show Less</>
              ) : (
                <><ChevronDown className="w-4 h-4" />Show More</>
              )}
            </button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAdditionalInfo = () => {
    if (!device.attributes) return null;

    // Props to pass to device-specific components
    const deviceProps = {
      device,
      parsedAttributes,
      statusLabel,
      siteObjectKey: deviceKey, // Pass site device key for KPI API calls
    };

    // Render device-type specific components
    switch (device.equipmentType) {
      case "RIM":
        return <RimDeviceDetails {...deviceProps} />;
      case "ACM":
        return <AcmDeviceDetails {...deviceProps} />;
      case "HCM":
        return <HcmDeviceDetails {...deviceProps} />;
      case "OIM":
        return <OimDeviceDetails {...deviceProps} />;
      case "RAU":
        return <RauDeviceDetails {...deviceProps} />;
      case "RXU":
      case "RXU67":
        return <RxuDeviceDetails {...deviceProps} />;
      case "RIU":
        return <RiuDeviceDetails {...deviceProps} />;
      case "SRIU":
      case "S-RIU":
      case "sRIU":
        return <SRiuDeviceDetails {...deviceProps} />;
      case "MRU":
        return <MruDeviceDetails {...deviceProps} />;
     case "MXU":
        return <MxuDeviceDetails {...deviceProps} />;
      case "FCM":
      case "FMM":
      case "FRM":
        return <FcmDeviceDetails {...deviceProps} />;
      case "OCH":
        return <OchDeviceDetails {...deviceProps} />;
      case "DXRU":
      case "DxRU":
        return <DxruDeviceDetails {...deviceProps} />;
      case "DXRU10G":
      case "DXRU-10G":
      case "DxRU-10G":
      case "DxRU10G":
        return <Dxru10gDeviceDetails {...deviceProps} />;
      case "PSU":
      case "Psu":
        return <PsuDeviceDetails {...deviceProps} />;
      case "SPSU":
      case "S-PSU":
      case "sPSU":
      case "SPsu":
        return <SpsuDeviceDetails {...deviceProps} />;
      case "DEU":
        return <DeuDeviceDetails {...deviceProps} />;
      case "DCU":
        return <DcuDeviceDetails {...deviceProps} />;
      case "SDCU":
      case "S-DCU":
      case "sDCU":
        return <SDcuDeviceDetails {...deviceProps} />;
      default:
        if (isAuDevice)  return <AuDeviceDetails {...deviceProps} />;
        if (isA3Device)  return <A3DeviceDetails {...deviceProps} />;
        if (isRu2Device) return <Ru2DeviceDetails {...deviceProps} />;
        if (isRu3Device) return <Ru3DeviceDetails {...deviceProps} />;
        if (isE2Device)  return <E2DeviceDetails {...deviceProps} />;
        if (isE3Device) {
          if (parsedAttributes?.deuAlarms !== undefined || parsedAttributes?.deuAlarmsMask !== undefined) {
            return <DeuDeviceDetails {...deviceProps} />;
          }
          return <E3DeviceDetails {...deviceProps} />;
        }
        if (isDeuDevice) return <DeuDeviceDetails {...deviceProps} />;
        return (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: "var(--foreground)" }}>
              Additional Information
            </Typography>
            <DetailRow label="Attributes" value={device.attributes} />
          </Box>
        );
    }
  };

  // Check if it's an AU, RU2, E2, or E3 device for wider drawer
  const isWideDevice = isAuDevice || isA3Device || isRu2Device || isRu3Device || isE2Device || isE3Device;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          width: isDxru10gDevice || isDeuDevice
              ? { xs: "100%", sm: "999px", md: "1198px" }
              : isRiuDevice
                ? { xs: "100%", sm: "825px", md: "990px" }
              : isWideDevice
                ? "95%" // 95% of screen width for AU/A2/RU2 devices
              : { xs: "100%", sm: "750px", md: "900px" },
          height: "100%",
          top: 0,
          backgroundColor: "var(--color-background)",
          borderLeft: "1px solid var(--color-border)",
          borderRadius: "10px 0 0 10px",
          boxShadow: "-4px 0 24px 0 rgba(0,0,0,0.08)",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div className="relative shrink-0 px-4 pt-2 pb-2 border-b border-border bg-background">
          {/* Close button */}
          <button
            type="button"
            className="absolute right-2 top-2 h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-accent text-foreground border-0 p-0 focus-visible:outline-none"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="close" size={20} />
          </button>

          {/* Title + badges + topology — no gap between rows */}
          <div className="flex flex-col pr-10">
            {/* Title row: name left, refresh right */}
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-xl font-semibold truncate text-foreground leading-tight">
                {device.hostname || device.equipmentType || device.managedObject || "Device Details"}
              </h2>
              <Tooltip title={formatRefreshTooltip(liveDevice.updatedAt)}>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Last refresh: {formatRefreshLabel(liveDevice.updatedAt)}
                  </span>
                  <Chip
                    size="small"
                    label="Auto-refresh: 10s"
                    variant="outlined"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      borderColor: "var(--border)",
                      color: "var(--muted-foreground)",
                    }}
                  />
                </div>
              </Tooltip>
            </div>

            {/* Badges & status row — topology path floated right */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                {device.equipmentType && (
                  <Badge variant="secondary" className="font-normal">
                    {device.equipmentType}
                  </Badge>
                )}
                {device.status && <StatusIcon status={statusLabel} />}
                {chassisTypeName && device.equipmentType === 'ACM' && (
                  <Badge variant="secondary" className="font-normal">
                    Chassis: {chassisTypeName}
                  </Badge>
                )}
                {device.equipmentType === 'RIM' && device.band && device.band.split(',').map((band: string, index: number) => (
                  <Badge key={index} variant="outline" className="font-normal">
                    {band.trim()}
                  </Badge>
                ))}
              </div>

              {/* Topology path — right side of badge row */}
              {shouldShowTopologyPath && (
                <div className="shrink-0">
                  <DeviceTopologyPath
                    device={device}
                    parsedAttributes={parsedAttributes}
                    chassisTypeName={chassisTypeName}
                    allEquipment={allEquipment}
                    selectedObjectKey={selectedObjectKey || undefined}
                    onSegmentClick={(objectKey) => setSelectedObjectKey(objectKey)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", px: 5, py: 3 }}>
          {/* Summary Card - Consolidated device information (skip for AU/RU2/RU3/E2/E3/A3/DEU devices as they have their own sections) */}
          {!isAuDevice && !isA3Device && !isRu2Device && !isRu3Device && !isE2Device && !isE3Device && !isDeuDevice && renderSummary()}
          
          {/* Device-type specific details */}
          {renderAdditionalInfo()}

          {/* Notes Section */}
          {device.remarks && (
            <Card className="mt-2 mb-3">
              <CardContent className="px-4 py-4">
                <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-4">
                  Notes
                </p>
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {device.remarks}
                </p>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default DasDeviceDetailsDrawer;
